import React from 'react';
import Moment from 'moment';

import { DATE_FORMAT, toUniversalDate } from '../../../utils/date.utils';
import {
  checkDateBounds,
  getDateInputPlaceholder,
  maskDateInput,
  parseTypedDate,
} from '../../../utils/dateInput.utils';
import { formatDateForLocale } from '../../../utils/hooks/useDateFormat';
import type { RetroDateValue, RetroPreset } from './retroDateField.types';

const ISO_DATE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function isIsoDateValue(value: RetroDateValue): value is string {
  return typeof value === 'string' && ISO_DATE_PATTERN.test(value);
}

type UseRetroDateFieldParams = {
  value: RetroDateValue;
  presets: RetroPreset[];
  minDate?: Date;
  maxDate?: Date;
  onChange: (value: RetroDateValue) => void;
};

/**
 * Interaction state for a retro-date control that can be typed into as well as
 * picked from a calendar.
 *
 * Commit points are explicit — Enter and blur — so a half-typed date is never
 * pushed upstream, and an entry that fails to parse or falls outside the
 * allowed range leaves the previously committed value untouched.
 */
export function useRetroDateField({
  value,
  presets,
  minDate,
  maxDate,
  onChange,
}: UseRetroDateFieldParams) {
  const committedText = useCommittedText(value, presets);

  const [text, setText] = React.useState(committedText);
  const [error, setError] = React.useState<string | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  // Re-sync when the value changes upstream, e.g. a rater response or a reset.
  React.useEffect(() => {
    setText(committedText);
    setError(null);
  }, [committedText]);

  const placeholder = getDateInputPlaceholder();
  const rangeLabel = buildRangeLabel(minDate, maxDate);

  const openCalendar = () => setIsCalendarOpen(true);
  const closeCalendar = () => setIsCalendarOpen(false);

  const commitValue = (nextValue: RetroDateValue) => {
    setError(null);
    closeCalendar();
    onChange(nextValue);
  };

  const selectDate = (date: Date) => {
    commitValue(Moment(date).format(DATE_FORMAT));
  };

  const selectPreset = (preset: RetroPreset) => {
    commitValue(preset.value);
  };

  const revert = () => {
    setText(committedText);
    setError(null);
    closeCalendar();
  };

  /**
   * Presets are typeable too, so free text is only masked into a date once it
   * stops looking like the name of a period.
   */
  const changeText = (nextText: string) => {
    setText(
      looksLikePresetName(nextText, presets)
        ? nextText
        : maskDateInput(nextText)
    );

    if (error) {
      setError(null);
    }
  };

  const commitText = (rawText: string) => {
    const trimmed = rawText.trim();

    if (!trimmed) {
      setError('Enter a retroactive date, or choose a period.');
      return;
    }

    const matchedPreset = findPresetByLabel(trimmed, presets);
    if (matchedPreset) {
      commitValue(matchedPreset.value);
      return;
    }

    const parsed = parseTypedDate(trimmed);
    if (parsed.status !== 'ok') {
      setError(`Enter a complete date as ${placeholder}.`);
      return;
    }

    const bounds = checkDateBounds(parsed.date, minDate, maxDate);
    if (bounds !== 'ok') {
      setError(`Date must be ${rangeLabel}.`);
      return;
    }

    selectDate(parsed.date);
  };

  const isDirty = text.trim() !== committedText;

  /**
   * Blur fires whenever focus moves into the calendar popover, so an untouched
   * field must not be re-validated — that would flag an empty control the user
   * merely clicked through.
   */
  const commitOnBlur = (rawText: string) => {
    if (rawText.trim() === committedText) {
      return;
    }

    commitText(rawText);
  };

  const hint = isDirty
    ? 'Press Enter to apply.'
    : `Type a date ${rangeLabel}, or pick a period.`;

  return {
    text,
    error,
    hint,
    placeholder,
    isCalendarOpen,
    isDirty,
    changeText,
    commitText,
    commitOnBlur,
    revert,
    selectDate,
    selectPreset,
    openCalendar,
    closeCalendar,
  };
}

/** The text shown for the currently committed value. */
function useCommittedText(value: RetroDateValue, presets: RetroPreset[]) {
  return React.useMemo(() => {
    if (isIsoDateValue(value)) {
      return formatDateForLocale(Moment(value, DATE_FORMAT).toDate());
    }

    const preset = presets.find((option) => option.value === value);
    if (preset) {
      return preset.label;
    }

    return value === null || value === undefined ? '' : String(value);
  }, [value, presets]);
}

function findPresetByLabel(text: string, presets: RetroPreset[]) {
  return presets.find(
    (preset) => preset.label.toLowerCase() === text.toLowerCase()
  );
}

/**
 * Preset labels can start with a digit ("1 Year", "3 Years"), so this cannot
 * key off the presence of letters — that would mask the leading "1" and strip
 * the space before the user ever reaches the "Y", making numeric presets
 * impossible to type.
 */
function looksLikePresetName(text: string, presets: RetroPreset[]) {
  const candidate = text.trim().toLowerCase();

  if (!candidate) {
    return false;
  }

  return presets.some((preset) =>
    preset.label.toLowerCase().startsWith(candidate)
  );
}

function buildRangeLabel(minDate?: Date, maxDate?: Date) {
  const min = minDate && toUniversalDate(minDate);
  const max = maxDate && toUniversalDate(maxDate);

  if (min && max) {
    return `between ${min} and ${max}`;
  }

  if (min) {
    return `on or after ${min}`;
  }

  if (max) {
    return `on or before ${max}`;
  }

  return 'in the allowed range';
}
